import { Component, OnDestroy, OnInit } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { NotificationState } from 'app/notifications/notification-state';
import { DashboardWidget } from 'app/viewmodels/dashboard/dashboard-widget';
import { DropdownItem } from 'app/viewmodels/dropdown-item';
import { OptimizationType } from 'app/viewmodels/equipment/enums/optimization-type';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { PrimeNGConfig, TreeNode } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DashboardService } from '../../dashboard.service';
import { RiskDistributionParameterType } from '../riskdistribution-parameter-type';
import { WidgetDataType } from '../widget-data-type';
import { WidgetConfigurationService } from './widget-configuration-dialog.service';
;

@Component({
  selector: 'hb-widget-configuration-dialog',
  templateUrl: './widget-configuration-dialog.component.html',
  styleUrls: ['./widget-configuration-dialog.component.scss'],
})
export class WidgetConfigurationDialogComponent implements OnInit, OnDestroy {
  private unsubscribe: Subject<boolean> = new Subject();
  public widgetDataType: WidgetDataType;

  public hierarchyNodes: TreeNode[] = [];
  public selectedHierarchyNodes: [] = [];

  public riskDistributionParameterType = RiskDistributionParameterType;
  public dashboardWidget: DashboardWidget;

  public selectedOptimizationType: number = this.setSelectedOptimization();
  public optimizationTypes = [
    new DropdownItem(this.i18NextPipe.transform('Widgets.ShowAll'), 0),
    new DropdownItem(this.i18NextPipe.transform('Widgets.Availability'), OptimizationType.Availability),
    new DropdownItem(this.i18NextPipe.transform('Widgets.Commercial'), OptimizationType.Commercial),
    new DropdownItem(this.i18NextPipe.transform('Widgets.EngineeringJudgement'), OptimizationType.EngineeringJudgement),
    new DropdownItem(this.i18NextPipe.transform('Widgets.Functionality'), OptimizationType.Functionality),
    new DropdownItem(this.i18NextPipe.transform('Widgets.Reliability'), OptimizationType.Reliability),
    new DropdownItem(this.i18NextPipe.transform('Widgets.Survivability'), OptimizationType.Survivability),
  ];

  public noModalOverflow: boolean = false;

  public dateRangeStartValue: Date;
  public dateRangeEndValue: Date;

  constructor(public bsModalRef: BsModalRef, public widgetConfigService: WidgetConfigurationService
    , public dashboardService: DashboardService, private i18NextPipe: I18NextPipe,
    private primengConfig: PrimeNGConfig,
  ) { }

  public ngOnInit() {
    this.primengConfig.overlayOptions = {
      showTransitionOptions: '0ms linear',
      hideTransitionOptions: '0ms linear',
    };

    this.dashboardWidget = this.widgetConfigService.dashboardWidget;
    const widgetDataType = this.dashboardWidget && this.dashboardWidget.widget ? this.dashboardWidget.widget.widgetDataType : undefined;

    this.initWidgetConfig(widgetDataType);
    this.dateRangeStartValue = this.widgetConfigService.dateRangeStartParameter && this.widgetConfigService.dateRangeStartParameter.dateTimeValue;
    this.dateRangeEndValue = this.widgetConfigService.dateRangeEndParameter && this.widgetConfigService.dateRangeEndParameter.dateTimeValue;
    this.setNoModalOverflow();
  }

  public ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  public getSelectedHierarchyNodes() {
    return this.widgetConfigService.selectedHierarchyNodes;
  }

  private initWidgetConfig(widgetDataType: WidgetDataType): void {
    this.widgetConfigService
      .getHierarchyTreeNodes(`${widgetDataType}`)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        treeNodes => {
          this.widgetConfigService.initialiseWidgetConfig(treeNodes);
        },
        () => this.widgetConfigService.showNotification(this.i18NextPipe.transform('Widgets.CouldNotRetrieveHierarchySelection'), NotificationState.Failed),
      );
  }

  get widgetDataTypeEnum() {
    return WidgetDataType;
  }

  public cancel(): void {
    this.bsModalRef.hide();
  }

  public apply(): void {
    this.widgetConfigService.updateWidget(this.selectedOptimizationType);
    const { dateRangeStartParameter, dateRangeEndParameter } = this.widgetConfigService;

    if (dateRangeStartParameter) {
      this.widgetConfigService.dateRangeStartParameter.dateTimeValue = this.dateRangeStartValue;
    }

    if (dateRangeEndParameter) {
      this.widgetConfigService.dateRangeEndParameter.dateTimeValue = this.dateRangeEndValue;
    }

    this.bsModalRef.hide();
  }

  public delete(): void {
    this.dashboardService.deleteWidget(this.dashboardWidget.widgetId);
    this.bsModalRef.hide();
  }

  optimzationTypeChanged(value: string): void {
    const valueAsNumber = Number(value);

    if (Number.isInteger(valueAsNumber)) {
      this.selectedOptimizationType = valueAsNumber;
    }
  }

  private setSelectedOptimization(): number {
    return this.widgetConfigService.optimizationTypeParameter ? this.widgetConfigService.optimizationTypeParameter.intValue : 0;
  }

  private setNoModalOverflow(): void {
    if (this.dashboardWidget && this.dashboardWidget.widget) {
      this.noModalOverflow = this.dashboardWidget.widget.widgetDataType === this.widgetDataTypeEnum.PlannedMaintenanceImprovements;
    }
  }
}
